/**
 * Avatar — round initials badge (design-revamp / handoff-2026-06).
 *
 * Production translation of the prototype `Avatar`
 * (`docs/GardEn/handoff-2026-06/garden/ui.jsx`): a round colored disc with
 * white, bold, centered initials. Font size scales with the disc (0.36×,
 * matching the prototype). The `color` may be any CSS colour string —
 * fixtures (`garden/data.jsx`) supply per-person colours, so this prop is an
 * intentional exception to the token-only rule (it is data, not chrome); it
 * defaults to the `--g-primary` token.
 */
import type { ReactElement } from "react";

export interface AvatarProps {
  /** Initials to render (typically 1–2 characters). */
  initials: string;
  /** Background colour — any CSS colour. Defaults to `var(--g-primary)`. */
  color?: string;
  /** Disc diameter in px. Defaults to 40 (prototype default). */
  size?: number;
}

/**
 * Renders a round initials avatar. `data-testid="g-avatar"`. The disc is
 * decorative styling around the initials text, which remains readable to
 * assistive tech.
 */
export function Avatar({
  initials,
  color = "var(--g-primary)",
  size = 40,
}: AvatarProps): ReactElement {
  return (
    <div
      data-testid="g-avatar"
      className="grid shrink-0 place-items-center rounded-full font-bold tracking-[-0.01em] text-white"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.36,
        background: color,
      }}
    >
      {initials}
    </div>
  );
}
